import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/ru';
import i18n from 'i18n';
// Add more locales as needed

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

/**
 * Validate if a locale string is valid
 */
function isValidLocale(locale: string): boolean {
  if (!locale || locale.length < 2) return false;

  try {
    // Test if the locale is valid by trying to use it
    new Intl.DateTimeFormat(locale).format(new Date());
    return true;
  } catch (error) {
    return false;
  }
}

export type preferredTimeDisplayModeTypes = 'utc' | 'configured' | 'local';

/**
 * Get the user's locale from active i18n language, browser, or fallback to 'en-US'
 */
export function getUserLocale(): string {
  if (i18n.language) {
    if (i18n.language === 'ru') return 'ru-RU';
    if (i18n.language === 'es') return 'es-ES';
    if (i18n.language === 'fr') return 'fr-FR';
    if (i18n.language === 'en') return 'en-US';
    return i18n.language;
  }
  if (typeof window !== 'undefined') {
    return navigator.language || navigator.languages?.[0] || 'en-US';
  }
  return 'en-US';
}

/**
 * Get locale-specific date format using Intl.DateTimeFormat
 */
export function getLocaleDateFormat(locale?: string): string {
  const userLocale = locale ?? getUserLocale();

  // Create a sample date to determine the format
  const sampleDate = new Date(2023, 11, 25); // December 25, 2023

  const formatter = new Intl.DateTimeFormat(userLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(sampleDate);

  // Build dayjs format string based on the order of parts
  let format = '';
  for (const part of parts) {
    switch (part.type) {
      case 'day':
        format += 'DD';
        break;
      case 'month':
        format += 'MM';
        break;
      case 'year':
        format += 'YYYY';
        break;
      case 'literal':
        format += part.value;
        break;
    }
  }

  return format;
}

/**
 * Get locale-specific date-time format
 */
export function getLocaleDateTimeFormat(locale?: string): string {
  const dateFormat = getLocaleDateFormat(locale ?? getUserLocale());
  const timeFormat = 'HH:mm';
  return `${dateFormat} ${timeFormat}`;
}

/**
 * Get locale-specific date-time format with seconds
 */
export function getLocaleDateTimeFormatWithSeconds(locale?: string): string {
  const dateFormat = getLocaleDateFormat(locale ?? getUserLocale());

  const timeFormat = 'HH:mm:ss';
  return `${dateFormat} ${timeFormat}`;
}

/**
 * Get MUI-compatible date format based on locale
 */
export function getMuiDateFormat(locale?: string): string {
  try {
    const userLocale = locale ?? getUserLocale();

    // Validate locale
    if (!isValidLocale(userLocale)) {
      return 'DD/MM/YYYY';
    }

    const format = getLocaleDateFormat(userLocale);

    // Convert dayjs format to MUI format
    // MUI uses different format tokens than dayjs
    return format
      .replace(/DD/g, 'DD') // day
      .replace(/YYYY/g, 'YYYY'); // year
  } catch (error) {
    console.warn('Error in getMuiDateFormat:', error);
    return 'DD/MM/YYYY';
  }
}
